import { useState, useEffect } from 'react';
import { useQuota } from '../../../context/QuotaContext';
import { RiBrainLine } from 'react-icons/ri';
import { MdImage } from 'react-icons/md';
import { BiUpload } from 'react-icons/bi';
import { MdInfoOutline } from 'react-icons/md';
import { useNavigate } from 'react-router-dom';
import { MdWarning } from 'react-icons/md';

// Tooltip descriptions for each quota type
const QUOTA_DESCRIPTIONS = {
  gpt: "AI credits for text generation",
  image: "AI credits for image generation",
  upload: "Number of content uploads",
  twitter: "Daily X/Twitter posts limit"
};

// Helper function to format reset time
const formatResetTime = (resetTime) => {
  if (!resetTime) return '';
  
  const resetDate = new Date(resetTime);
  const now = new Date();
  
  // Calculate time difference
  const timeDiff = resetDate.getTime() - now.getTime();
  
  // If reset time is in the past, return empty string
  if (timeDiff <= 0) return '';
  
  const days = Math.floor(timeDiff / (1000 * 60 * 60 * 24));
  const hours = Math.floor((timeDiff % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
  const minutes = Math.floor((timeDiff % (1000 * 60 * 60)) / (1000 * 60));
  
  if (days > 0) {
    return `${days} day${days > 1 ? 's' : ''}`;
  } else if (hours > 0) {
    return `${hours} hour${hours > 1 ? 's' : ''}`;
  } else if (minutes > 0) {
    return `${minutes} minute${minutes > 1 ? 's' : ''}`;
  } else {
    return 'less than a minute';
  }
};

/**
 * UsageLimits Component - A reusable component to display usage limits
 * 
 * @param {Object} props
 * @param {boolean} props.showGpt - Whether to show GPT usage limits
 * @param {boolean} props.showImage - Whether to show image generation limits
 * @param {boolean} props.showUpload - Whether to show upload limits
 * @param {boolean} props.showTwitter - Whether to show Twitter posting limits
 * @param {boolean} props.showLabels - Whether to show labels for each limit type
 * @param {string} props.variant - Display variant ('compact', 'full', 'pill')
 * @param {string} props.className - Additional CSS classes
 * @param {Function} props.onQuotaFetched - Callback when quota data is fetched
 * @param {Function} props.onGptLimitReached - Callback when GPT limit is reached
 * @param {any} props.refreshTrigger - When this value changes, the quota will be refreshed
 */
export default function UsageLimits({ 
  showGpt = true, 
  showImage = false, 
  showUpload = false,
  showTwitter = false,
  showLabels = false,
  variant = 'pill',
  className = '',
  onQuotaFetched = null,
  onGptLimitReached = null,
  refreshTrigger = null
}) {
  const [activeTooltip, setActiveTooltip] = useState(null);
  const navigate = useNavigate();
  
  // Use shared QuotaContext
  const { quota, loading, error, fetchQuota } = useQuota();

  // Fetch quota information on component mount
  useEffect(() => {
    const quotaTypes = {
      showGpt,
      showImage,
      showUpload,
      showTwitter
    };
    
    // Only fetch if we don't have the required quota data
    const needsGpt = showGpt && !quota?.gpt;
    const needsImage = showImage && !quota?.image;
    const needsUpload = showUpload && !quota?.upload;
    const needsTwitter = showTwitter && !quota?.twitter;
    
    if (needsGpt || needsImage || needsUpload || needsTwitter) {
      fetchQuota(quotaTypes);
    }
  }, [showGpt, showImage, showUpload, showTwitter, quota, fetchQuota]);
  
  // Call onQuotaFetched callback when quota data is available
  useEffect(() => {
    if (quota && onQuotaFetched) {
      onQuotaFetched(quota);
    }
  }, [quota, onQuotaFetched]);

  // Check if GPT limit is reached and notify parent component
  useEffect(() => {
    if (quota?.gpt && showGpt && onGptLimitReached) {
      // Check if user has reached their limit (used >= limit)
      const isLimitReached = !quota.gpt.unlimited && quota.gpt.used >= quota.gpt.limit;
      onGptLimitReached(isLimitReached);
    }
  }, [quota, showGpt, onGptLimitReached]);

  // Check if any usage is reaching critical levels (80% or higher)
  const hasHighUsage = () => {
    if (!quota) return false;
    
    const checkUsage = (data) => {
      if (!data || data.unlimited) return false;
      const usagePercentage = (data.used / data.limit) * 100;
      return usagePercentage >= 80;
    };
    
    return (showGpt && checkUsage(quota.gpt)) || 
           (showImage && checkUsage(quota.image)) || 
           (showUpload && checkUsage(quota.upload)) ||
           (showTwitter && checkUsage(quota.twitter));
  };

  // Render a single quota item
  const renderQuotaItem = (type, icon, data) => {
    if (!data) return null;
    
    // Function to handle tooltip toggle
    const toggleTooltip = (tooltipType) => {
      if (activeTooltip === tooltipType) {
        setActiveTooltip(null);
      } else {
        setActiveTooltip(tooltipType);
      }
    };
    
    // Function to determine color based on usage percentage
    const getColorByUsage = (usedAmount, limit) => {
      if (data.unlimited) return 'bg-blue-200 border border-blue-300';
      
      const usagePercentage = (usedAmount / limit) * 100;
      if (usagePercentage < 50) return '--theme-colors';
      if (usagePercentage >= 80) return 'bg-red-200 border border-red-300';
      if (usagePercentage >= 50) return 'bg-yellow-200 border border-yellow-300';
      return 'bg-blue-200';
    };
    
    // Different display variants
    if (variant === 'compact') {
      return (
        <div 
          className="flex items-center mr-2 --theme-colors relative group" 
          key={type}
          onMouseEnter={() => setActiveTooltip(type)}
          onMouseLeave={() => setActiveTooltip(null)}
        >
          <span className="mr-1">{icon}</span>
          <span className="text-xs">
            {data.unlimited ? '∞' : data.used}
          </span>
          
          <MdInfoOutline className="ml-1 h-3 w-3 text-gray-400 cursor-help" />
          
          {activeTooltip === type && (
            <div className="absolute top-full mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[9999] whitespace-nowrap tooltip-right">
              {QUOTA_DESCRIPTIONS[type]}
              <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-800"></div>
            </div>
          )}
        </div>
      );
    } else if (variant === 'pill') {
      return (
        <div 
          className={`flex items-center space-x-1 py-1 px-3 rounded-full mr-2 relative group cursor-pointer ` + getColorByUsage(data.used, data.limit)}
          key={type}
          onMouseEnter={() => setActiveTooltip(type)}
          onMouseLeave={() => setActiveTooltip(null)}
          onClick={() => {
            navigate(`/dashboard/settings/subscription`);
          }}
        >
          {icon}
          <span className="text-xs font-medium !text-gray-600">
            {data.unlimited ? (
              'Unlimited'
            ) : (
              <>
                {data.used} / {data.limit}
                {showLabels && <span className="ml-1 text-gray-400 text-xs">{type}</span>}
              </>
            )}
          </span>
          
          <MdInfoOutline className="ml-1 h-3 w-3 text-gray-400 cursor-help" />
          
          {activeTooltip === type && (
            <div className="absolute top-full mt-2 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[9999] whitespace-nowrap tooltip-right">
              {QUOTA_DESCRIPTIONS[type]}
              <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-800"></div>
            </div>
          )}
        </div>
      );
    } else {
      // Full variant
      const usageColor = getColorByUsage(data.used, data.limit);
      return (
        <div className="flex flex-col mb-2 relative" key={type}>
          <div 
            className="flex items-center mb-1 group"
            onMouseEnter={() => setActiveTooltip(type)}
            onMouseLeave={() => setActiveTooltip(null)}
          >
            {icon}
            <span className="text-sm font-medium ml-1">
              {showLabels ? `${type.charAt(0).toUpperCase() + type.slice(1)} Credits` : ''}
            </span>
            
            <MdInfoOutline className="ml-1 h-3 w-3 text-gray-400 cursor-help" />
            
            {activeTooltip === type && (
              <div className="absolute top-full left-0 mt-1 px-3 py-2 bg-gray-800 text-white text-xs rounded shadow-lg z-[9999] whitespace-nowrap">
                {QUOTA_DESCRIPTIONS[type]}
                <div className="absolute bottom-full left-4 border-4 border-transparent border-b-gray-800"></div>
              </div>
            )}
          </div>
          <div className="w-full rounded-full h-2">
            <div 
              className={`${usageColor} h-2 rounded-full`}
              style={{ 
                width: data.unlimited ? '100%' : `${(data.used / data.limit) * 100}%` 
              }}
            ></div>
          </div>
          <div className="flex justify-between text-xs mt-1">
            <span>{data.unlimited ? 'Unlimited' : `${data.used} used`}</span>
            <span>{data.unlimited ? '' : `${data.remaining} remaining`}</span>
          </div>
        </div>
      );
    }
  };

  // Warning message when usage is high
  const renderWarningMessage = () => {
    if (!hasHighUsage()) return null;
    
    // Check if any of the quotas are completely maxed out
    const hasMaxedOutQuota = () => {
      if (!quota) return false;
      
      const checkMaxedOut = (data) => {
        if (!data || data.unlimited) return false;
        return data.used >= data.limit;
      };
      
      return (showGpt && checkMaxedOut(quota.gpt)) || 
             (showImage && checkMaxedOut(quota.image)) || 
             (showUpload && checkMaxedOut(quota.upload)) ||
             (showTwitter && checkMaxedOut(quota.twitter));
    };
    
    const isMaxedOut = hasMaxedOutQuota();
    
    // Get reset time information for maxed out quotas
    const getResetTimeInfo = () => {
      const resetTimes = [];
      
      if (showGpt && quota.gpt && !quota.gpt.unlimited && quota.gpt.used >= quota.gpt.limit && quota.gpt.resetTime) {
        const timeLeft = formatResetTime(quota.gpt.resetTime);
        if (timeLeft) resetTimes.push(`AI credits reset in ${timeLeft}`);
      }
      
      if (showImage && quota.image && !quota.image.unlimited && quota.image.used >= quota.image.limit && quota.image.resetTime) {
        const timeLeft = formatResetTime(quota.image.resetTime);
        if (timeLeft) resetTimes.push(`Image credits reset in ${timeLeft}`);
      }
      
      if (showUpload && quota.upload && !quota.upload.unlimited && quota.upload.used >= quota.upload.limit && quota.upload.resetTime) {
        const timeLeft = formatResetTime(quota.upload.resetTime);
        if (timeLeft) resetTimes.push(`Upload limit resets in ${timeLeft}`);
      }
      
      if (showTwitter && quota.twitter && !quota.twitter.unlimited && quota.twitter.used >= quota.twitter.limit && quota.twitter.resetTime) {
        const timeLeft = formatResetTime(quota.twitter.resetTime);
        if (timeLeft) resetTimes.push(`Twitter limit resets in ${timeLeft}`);
      }
      
      return resetTimes;
    };
    
    const resetTimeInfo = getResetTimeInfo();
    
    return (
      <div className="mt-2 text-xs bg-red-50 text-red-700 p-2 rounded-md border border-red-200">
        <div className="flex items-center mb-1">
          <MdWarning className="h-4 w-4 mr-1 text-red-500" />
          <span>{isMaxedOut ? "You've reached your maximum usage limit!" : "You're approaching your usage limit!"}</span>
        </div>
        {resetTimeInfo.length > 0 && (
          <div className="text-xs text-red-600 mb-1">
            {resetTimeInfo.map((info, index) => (
              <div key={index}>{info}</div>
            ))}
          </div>
        )}
        <button 
          className="underline font-medium text-red-700 hover:text-red-800" 
          onClick={() => navigate('/dashboard/settings/subscription')}
        >
          Upgrade now
        </button>
      </div>
    );
  };

  // Loading state
  if (loading && !quota) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="flex items-center space-x-1">
          <RiBrainLine className="h-4 w-4 text-gray-400" />
          <span className="text-xs text-gray-400">Loading...</span>
        </div>
      </div>
    );
  }

  // Error state
  if (error && !quota) {
    return (
      <div className={`flex items-center ${className}`}>
        <div className="text-xs text-red-500">
          {error}
        </div>
      </div>
    );
  }

  // If quota data is not available yet
  if (!quota) return null;

  // Check if upload quota is maxed out
  const isUploadMaxedOut = quota.upload && !quota.upload.unlimited && quota.upload.used >= quota.upload.limit;
  
  // Hide Twitter counter if upload is maxed out since Twitter posting is also blocked
  const shouldShowTwitter = showTwitter && !isUploadMaxedOut;

  return (
    <div className={`${className} flex flex-col items-end`}>
      <div className={`flex ${variant === 'full' ? 'flex-col' : 'flex-row'}`}>
        {showGpt && quota.gpt && renderQuotaItem('gpt', <RiBrainLine className="h-4 w-4 text-blue-500" />, quota.gpt)}
        {showImage && quota.image && renderQuotaItem('image', <MdImage className="h-4 w-4 text-green-500" />, quota.image)}
        {showUpload && quota.upload && renderQuotaItem('upload', <BiUpload className="h-4 w-4 text-purple-500" />, quota.upload)}
        {shouldShowTwitter && quota.twitter && renderQuotaItem('twitter', (
          <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
            <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
          </svg>
        ), quota.twitter)}
      </div>
      {renderWarningMessage()}
    </div>
  );
} 